import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Search, Plus, BookOpen, Copy, Mail, Edit, Trash2,
  Phone, MessageSquare, Voicemail, Target,
  CheckCircle2, Variable, Sparkles, Loader2, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { MainLayout } from '../components/MainLayout';

const CATEGORY_CONFIG = {
  cold_call: { label: 'Cold Call', icon: Phone, color: 'bg-sky-500' },
  follow_up: { label: 'Follow-up', icon: MessageSquare, color: 'bg-emerald-500' },
  objection_handling: { label: 'Objection Handling', icon: Target, color: 'bg-amber-500' },
  closing: { label: 'Closing', icon: CheckCircle2, color: 'bg-violet-500' },
  voicemail: { label: 'Voicemail', icon: Voicemail, color: 'bg-slate-500' },
};

export const LibraryPage = () => {
  const { user, authFetch } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedScript, setSelectedScript] = useState(null);
  const [showPersonalise, setShowPersonalise] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [aiGenerating, setAIGenerating] = useState(false);
  const [variableValues, setVariableValues] = useState({});
  const [personalisedContent, setPersonalisedContent] = useState('');
  const [pipelineContacts, setPipelineContacts] = useState([]);
  
  const [editForm, setEditForm] = useState({
    title: '',
    category: 'cold_call',
    content: '',
    tags: '',
  });

  const [aiForm, setAIForm] = useState({
    client_type: 'contractor',
    sector: 'commercial_fitout',
    situation: 'cold_call',
    tone: 'professional',
    context: ''
  });

  useEffect(() => {
    loadScripts();
    loadContacts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContacts = async () => {
    try {
      const res = await authFetch('/pipeline/contacts');
      if (res.ok) {
        const data = await res.json();
        setPipelineContacts((data.contacts || []).sort((a, b) => (a.contact_name || '').localeCompare(b.contact_name || '')));
      }
    } catch { /* silent */ }
  };

  const loadScripts = async () => {
    try {
      const res = await authFetch('/scripts');
      if (res.ok) {
        const data = await res.json();
        setScripts(data.scripts || []);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (content, scriptId) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Copied to clipboard');
      
      // Track usage
      await authFetch(`/scripts/${scriptId}/use`, { method: 'POST' });
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const openEmailDraft = (content) => {
    const subject = encodeURIComponent('Following up on your project');
    const body = encodeURIComponent(content);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handlePersonalise = (script) => {
    setSelectedScript(script);
    const initialValues = {};
    (script.variables || []).forEach(v => {
      initialValues[v] = '';
    });
    setVariableValues(initialValues);
    setPersonalisedContent(script.content);
    setShowPersonalise(true);
  };

  const updatePersonalisedContent = () => {
    if (!selectedScript) return;
    let content = selectedScript.content;
    Object.entries(variableValues).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      content = content.replace(regex, value || `{{${key}}}`);
    });
    setPersonalisedContent(content);
  };

  useEffect(() => {
    updatePersonalisedContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableValues]);

  const saveScript = async () => {
    try {
      const payload = {
        title: editForm.title,
        category: editForm.category,
        content: editForm.content,
        tags: editForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      
      const method = selectedScript?.id ? 'PATCH' : 'POST';
      const url = selectedScript?.id ? `/scripts/${selectedScript.id}` : '/scripts';
      
      const res = await authFetch(url, { method, body: payload });
      if (res.ok) {
        toast.success('Script saved');
        setShowEdit(false);
        setSelectedScript(null);
        loadScripts();
      }
    } catch (err) {
      toast.error('Failed to save script');
    }
  };

  const deleteScript = async (scriptId) => {
    if (!window.confirm('Delete this script?')) return;
    try {
      const res = await authFetch(`/scripts/${scriptId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Script deleted');
        loadScripts();
      }
    } catch (err) {
      toast.error('Failed to delete script');
    }
  };

  const generateAIScript = async () => {
    setAIGenerating(true);
    try {
      const res = await authFetch('/scripts/generate-ai', {
        method: 'POST',
        body: aiForm
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('AI script generated successfully!');
        setShowAIGenerate(false);
        setAIForm({
          client_type: 'contractor',
          sector: 'commercial_fitout',
          situation: 'cold_call',
          tone: 'professional',
          context: ''
        });
        loadScripts();
        
        // Show the generated script
        setSelectedScript(data);
        setShowPersonalise(true);
        const initialValues = {};
        (data.variables || []).forEach(v => {
          initialValues[v] = '';
        });
        setVariableValues(initialValues);
        setPersonalisedContent(data.content);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate script');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      toast.error('Failed to generate AI script');
    } finally {
      setAIGenerating(false);
    }
  };

  const filteredScripts = scripts.filter(s => {
    const matchesSearch = 
      s.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = categoryFilter === 'all' || s.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const categoryCounts = Object.keys(CATEGORY_CONFIG).reduce((acc, cat) => {
    acc[cat] = scripts.filter(s => s.category === cat).length;
    return acc;
  }, {});

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="library-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-xl md:text-3xl font-bold text-[#1e3a5f]">
              Scripts Library
            </h1>
            <p className="text-slate-500 text-sm md:text-base mt-1">
              {scripts.length} scripts • Ready-to-use templates
            </p>
          </div>
          
          {(user?.role === 'director' || user?.role === 'senior_engineer') && (
            <div className="flex gap-2">
              <Button
                onClick={() => setShowAIGenerate(true)}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-full text-sm px-4"
                data-testid="ai-generate-btn"
              >
                <Sparkles className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">AI Generate</span>
                <span className="md:hidden">AI</span>
              </Button>
              <Button
                onClick={() => {
                  setSelectedScript(null);
                  setEditForm({ title: '', category: 'cold_call', content: '', tags: '' });
                  setShowEdit(true);
                }}
                variant="outline"
                className="rounded-full text-sm px-4"
                data-testid="new-script-btn"
              >
                <Plus className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Manual</span>
              </Button>
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              categoryFilter === 'all'
                ? 'bg-[#1e3a5f] text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All ({scripts.length})
          </button>
          {Object.entries(CATEGORY_CONFIG).map(([cat, config]) => {
            const Icon = config.icon;
            return (
              <button
                key={cat}
                onClick={() => setCategoryFilter(categoryFilter === cat ? 'all' : cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  categoryFilter === cat
                    ? 'bg-[#1e3a5f] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${cat}`}
              >
                <Icon className="w-4 h-4" />
                {config.label} ({categoryCounts[cat] || 0})
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search scripts by title, content, or tags..."
            className="pl-10 h-11 rounded-xl"
            data-testid="search-input"
          />
        </div>

        {/* Scripts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {filteredScripts.map(script => {
            const catConfig = CATEGORY_CONFIG[script.category] || CATEGORY_CONFIG.cold_call;
            const Icon = catConfig.icon;
            
            return (
              <div
                key={script.id}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`script-${script.id}`}
              >
                <div className="p-4 md:p-6">
                  <div className="flex items-start justify-between gap-3 mb-3 md:mb-4">
                    <div className={`w-8 h-8 md:w-10 md:h-10 ${catConfig.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                    </div>
                    {(user?.role === 'director') && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedScript(script);
                            setEditForm({
                              title: script.title,
                              category: script.category,
                              content: script.content,
                              tags: script.tags?.join(', ') || '',
                            });
                            setShowEdit(true);
                          }}
                          className="text-slate-400 hover:text-[#1e3a5f] h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteScript(script.id)}
                          className="text-slate-400 hover:text-red-600 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <h3 className="font-heading font-semibold text-[#1e3a5f] text-sm md:text-base mb-2 line-clamp-2">
                    {script.title}
                  </h3>
                  
                  <p className="text-xs md:text-sm text-slate-500 line-clamp-2 md:line-clamp-3 mb-3 md:mb-4">
                    {script.content}
                  </p>
                  
                  {script.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3 md:mb-4">
                      {script.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="chip chip-default text-xs">
                          {tag}
                        </span>
                      ))}
                      {script.tags.length > 3 && (
                        <span className="text-xs text-slate-400">+{script.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                  
                  {script.variables?.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-slate-400 mb-3 md:mb-4">
                      <Variable className="w-3 h-3" />
                      <span>{script.variables.length} variable{script.variables.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  
                  {script.is_ai_generated && (
                    <div className="flex items-center gap-1 text-xs text-violet-600 mb-3 md:mb-4">
                      <Sparkles className="w-3 h-3" />
                      <span>AI Generated</span>
                    </div>
                  )}
                </div>
                
                <div className="px-4 md:px-6 py-3 md:py-4 bg-slate-50 border-t border-slate-100 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(script.content, script.id)}
                    className="flex-1 rounded-full text-xs md:text-sm"
                    data-testid={`copy-${script.id}`}
                  >
                    <Copy className="w-4 h-4 md:mr-1" />
                    <span className="hidden md:inline">Copy</span>
                  </Button>
                  {script.variables?.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePersonalise(script)}
                      className="flex-1 rounded-full text-xs md:text-sm"
                      data-testid={`personalise-${script.id}`}
                    >
                      <Variable className="w-4 h-4 md:mr-1" />
                      <span className="hidden md:inline">Personalise</span>
                      <span className="md:hidden">Edit</span>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredScripts.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 md:p-12 text-center">
            <BookOpen className="w-10 h-10 md:w-12 md:h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-slate-700 mb-2">
              No scripts found
            </h3>
            <p className="text-sm text-slate-500">
              {searchQuery || categoryFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first script to get started'}
            </p>
          </div>
        )}

        {/* Personalise Modal */}
        <Dialog open={showPersonalise} onOpenChange={setShowPersonalise}>
          <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f]">
                Personalise Script
              </DialogTitle>
            </DialogHeader>
            
            {selectedScript && (
              <div className="space-y-6 mt-4">
                {/* Contact quick-fill */}
                {pipelineContacts.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      Fill from Contact
                    </Label>
                    <Select
                      onValueChange={(contactId) => {
                        const c = pipelineContacts.find(p => p.id === contactId);
                        if (!c) return;
                        setVariableValues(prev => ({
                          ...prev,
                          client_name: c.contact_name || prev.client_name || '',
                          company_name: c.company_name || prev.company_name || '',
                          location: c.sector?.replace(/_/g, ' ') || prev.location || '',
                          contact_name: c.contact_name || prev.contact_name || '',
                        }));
                      }}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="contact-fill-select">
                        <SelectValue placeholder="Select a contact to auto-fill..." />
                      </SelectTrigger>
                      <SelectContent>
                        {pipelineContacts.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.contact_name} — {c.company_name || 'No company'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {selectedScript.variables && selectedScript.variables.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedScript.variables.map(variable => (
                      <div key={variable} className="space-y-2">
                        <Label className="capitalize">{variable.replace('_', ' ')}</Label>
                        <Input
                          value={variableValues[variable] || ''}
                          onChange={(e) => setVariableValues(prev => ({
                            ...prev,
                            [variable]: e.target.value
                          }))}
                          placeholder={`Enter ${variable.replace('_', ' ')}`}
                          data-testid={`var-${variable}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Script Content</Label>
                  <div className="p-4 bg-slate-50 rounded-xl max-h-[40vh] overflow-y-auto">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {personalisedContent || selectedScript.content || 'No content available'}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowPersonalise(false)}
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      copyToClipboard(personalisedContent || selectedScript.content, selectedScript.id);
                      setShowPersonalise(false);
                    }}
                    className="flex-1 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button
                    onClick={() => {
                      openEmailDraft(personalisedContent || selectedScript.content);
                      setShowPersonalise(false);
                    }}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                </div>
              </div>
            )}
            
            {!selectedScript && (
              <div className="py-8 text-center text-slate-500">
                Loading script...
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f]">
                {selectedScript?.id ? 'Edit Script' : 'New Script'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={editForm.title}
                    onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Script title"
                    data-testid="edit-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={editForm.category}
                    onValueChange={(value) => setEditForm(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_CONFIG).map(([cat, config]) => (
                        <SelectItem key={cat} value={cat}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Content</Label>
                <p className="text-xs text-slate-500">
                  Use {"{{variable_name}}"} for personalisation variables
                </p>
                <Textarea
                  value={editForm.content}
                  onChange={(e) => setEditForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Script content..."
                  className="min-h-[200px]"
                  data-testid="edit-content"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tags (comma separated)</Label>
                <Input
                  value={editForm.tags}
                  onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="tag1, tag2, tag3"
                  data-testid="edit-tags"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowEdit(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveScript}
                  disabled={!editForm.title || !editForm.content}
                  className="flex-1 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
                >
                  Save Script
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* AI Generate Modal */}
        <Dialog open={showAIGenerate} onOpenChange={setShowAIGenerate}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                AI Script Generator
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-5 mt-4">
              <p className="text-sm text-slate-500">
                Generate a customised sales script using AI. Select your scenario and we&apos;ll create a ready-to-use script.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Type</Label>
                  <Select
                    value={aiForm.client_type}
                    onValueChange={(value) => setAIForm(prev => ({ ...prev, client_type: value }))}
                  >
                    <SelectTrigger data-testid="ai-client-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contractor">Main Contractor</SelectItem>
                      <SelectItem value="project_manager">Project Manager</SelectItem>
                      <SelectItem value="quantity_surveyor">Quantity Surveyor</SelectItem>
                      <SelectItem value="developer">Property Developer</SelectItem>
                      <SelectItem value="landlord">Landlord</SelectItem>
                      <SelectItem value="architect">Architect</SelectItem>
                      <SelectItem value="end_user">End User/Tenant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Sector</Label>
                  <Select
                    value={aiForm.sector}
                    onValueChange={(value) => setAIForm(prev => ({ ...prev, sector: value }))}
                  >
                    <SelectTrigger data-testid="ai-sector">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commercial_fitout">Commercial Fit-Out</SelectItem>
                      <SelectItem value="cat_a">CAT A Shell & Core</SelectItem>
                      <SelectItem value="cat_b">CAT B Fit-Out</SelectItem>
                      <SelectItem value="commercial_refurb">Commercial Refurb</SelectItem>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Situation</Label>
                  <Select
                    value={aiForm.situation}
                    onValueChange={(value) => setAIForm(prev => ({ ...prev, situation: value }))}
                  >
                    <SelectTrigger data-testid="ai-situation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cold_call">Cold Call</SelectItem>
                      <SelectItem value="follow_up">Follow-up</SelectItem>
                      <SelectItem value="objection_handling">Objection Handling</SelectItem>
                      <SelectItem value="closing">Closing</SelectItem>
                      <SelectItem value="voicemail">Voicemail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Tone</Label>
                  <Select
                    value={aiForm.tone}
                    onValueChange={(value) => setAIForm(prev => ({ ...prev, tone: value }))}
                  >
                    <SelectTrigger data-testid="ai-tone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="consultative">Consultative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Additional Context (Optional)</Label>
                <Textarea
                  value={aiForm.context}
                  onChange={(e) => setAIForm(prev => ({ ...prev, context: e.target.value }))}
                  placeholder="Any specific details about the prospect or project..."
                  className="min-h-[80px]"
                  data-testid="ai-context"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAIGenerate(false)}
                  className="flex-1 rounded-full"
                  disabled={aiGenerating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={generateAIScript}
                  disabled={aiGenerating}
                  className="flex-1 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-full"
                  data-testid="generate-ai-btn"
                >
                  {aiGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Script
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};
